import { Router } from "express";

import { teacherMobileController } from "../../controllers/mobile/teacher-mobile-controller";
import { authenticate, teacherOnly } from "../../middleware/auth-middleware";

const router = Router();

/**
 * Teacher Mobile Routes - Separate from school administrative flows
 * Base path: /api/mobile/teacher
 *
 * These routes are specifically designed for mobile applications
 * and do not interfere with school administrative workflows
 */

// Apply authentication middleware to all routes except public ones
router.use((req, res, next) => {
  // Allow public access to schools endpoint (for teacher signup)
  if (req.path === "/schools" && req.method === "GET") {
    next();
    return;
  }
  void authenticate(req, res, next);
});

// Apply teacher role validation to all routes except public ones
router.use((req, res, next) => {
  // Allow public access to schools endpoint (for teacher signup)
  if (req.path === "/schools" && req.method === "GET") {
    next();
    return;
  }
  teacherOnly(req, res, next);
});

/**
 * @route   POST /api/mobile/teacher/documents/remove
 * @desc    Remove specific documents from teacher profile using media IDs (Mobile App)
 * @access  Private - Teacher only
 * @body    { removeDocuments: string[] } - Array of media IDs to remove
 */
router.post(
  "/documents/remove",
  teacherMobileController.removeTeacherDocuments.bind(teacherMobileController),
);

/**
 * @route   POST /api/mobile/teacher/profile/update
 * @desc    Update teacher profile with document changes (Mobile App)
 * @access  Private - Teacher only
 * @body    { removeDocuments: string[] } - Array of media IDs to remove
 */
router.post(
  "/profile/update",
  teacherMobileController.updateTeacherProfile.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/classes
 * @desc    Get teacher's classes (Mobile App)
 * @access  Private - Teacher only
 */
router.get(
  "/classes",
  teacherMobileController.getClasses.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/students
 * @desc    Get teacher's students (Mobile App)
 * @access  Private - Teacher only
 */
router.get(
  "/students",
  teacherMobileController.getStudents.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/semesters
 * @desc    Get teacher's semesters (Mobile App)
 * @access  Private - Teacher only
 */
router.get(
  "/semesters",
  teacherMobileController.getSemesters.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/attendance
 * @desc    Get teacher's attendance records (Mobile App)
 * @access  Private - Teacher only
 */
router.get(
  "/attendance",
  teacherMobileController.getAttendance.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/classes/:classId/attendance
 * @desc    Get class students with attendance for a specific date (Mobile App)
 * @access  Private - Teacher only
 * @query   date (required) - Date in YYYY-MM-DD format
 */
router.get(
  "/classes/:classId/attendance",
  teacherMobileController.getClassAttendance.bind(teacherMobileController),
);

/**
 * @route   POST /api/mobile/teacher/classes/:classId/attendance
 * @desc    Mark attendance for a class (Mobile App)
 * @access  Private - Teacher only
 * @body    { date: string, attendance: [{ studentId, status, remarks? }] }
 */
router.post(
  "/classes/:classId/attendance",
  teacherMobileController.markClassAttendance.bind(teacherMobileController),
);

/**
 * @route   PUT /api/mobile/teacher/classes/:classId/attendance/:studentId
 * @desc    Update individual student attendance (Mobile App)
 * @access  Private - Teacher only
 * @body    { status: string, remarks?: string, date?: string }
 */
router.put(
  "/classes/:classId/attendance/:studentId",
  teacherMobileController.updateStudentAttendance.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/classes/:classId
 * @desc    Get class by ID with detailed information (Mobile App)
 * @access  Private - Teacher only
 */
router.get(
  "/classes/:classId",
  teacherMobileController.getClassById.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/schools
 * @desc    Get all schools for dropdown (Teacher Signup)
 * @access  Public
 */
router.get(
  "/schools",
  teacherMobileController.getAllSchools.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/classes/:classId/quizzes
 * @desc    Get quizzes for a class (Mobile App)
 * @access  Private - Teacher only
 */
router.get(
  "/classes/:classId/quizzes",
  teacherMobileController.getClassQuizzes.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/classes/:classId/assignments
 * @desc    Get assignments for a class (Mobile App)
 * @access  Private - Teacher only
 */
router.get(
  "/classes/:classId/assignments",
  teacherMobileController.getClassAssignments.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/quizzes/:quizId/submissions
 * @desc    Get quiz submissions with filters (Mobile App)
 * @access  Private - Teacher only
 * @query   filter (optional) - all, submitted, late_submitted, not_submitted
 */
router.get(
  "/quizzes/:quizId/submissions",
  teacherMobileController.getQuizSubmissions.bind(teacherMobileController),
);

/**
 * @route   GET /api/mobile/teacher/assignments/:assignmentId/submissions
 * @desc    Get assignment submissions with filters (Mobile App)
 * @access  Private - Teacher only
 * @query   filter (optional) - all, submitted, late_submitted, not_submitted
 */
router.get(
  "/assignments/:assignmentId/submissions",
  teacherMobileController.getAssignmentSubmissions.bind(
    teacherMobileController,
  ),
);

/**
 * @route   GET /api/mobile/teacher/reports
 * @desc    Get teacher reports/statistics (Mobile App)
 * @access  Private - Teacher only
 * @query   classId (optional), semesterId (optional), startDate (optional), endDate (optional)
 */
router.get(
  "/reports",
  teacherMobileController.getReports.bind(teacherMobileController),
);

export { router as teacherMobileRoutes };
